import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";

interface DropdownItem {
  label: string;
  to: string;
  desc?: string;
  icon?: string;
}

interface NavItem {
  label: string;
  to?: string;
  dropdown?: DropdownItem[];
}

const navItems: NavItem[] = [
  {
    label: "Shop",
    dropdown: [
      { label: "All Products", to: "/products", desc: "Browse our full collection", icon: "ri-store-2-line" },
      { label: "Chairs & Loungers", to: "/products?category=Chairs", desc: "Adirondack, lounge & accent chairs", icon: "ri-armchair-line" },
      { label: "Sofas & Sectionals", to: "/products?category=Sofas", desc: "Wicker sofas, loveseats & sectionals", icon: "ri-sofa-line" },
      { label: "Dining Tables", to: "/products?category=Tables", desc: "Teak, acacia & fire pit tables", icon: "ri-table-line" },
      { label: "Complete Sets", to: "/products?category=Sets", desc: "Coordinated furniture collections", icon: "ri-layout-grid-line" },
      { label: "Daybeds", to: "/products?category=Daybeds", desc: "Canopy daybeds & sun loungers", icon: "ri-hotel-bed-line" },
      { label: "Benches", to: "/products?category=Benches", desc: "Garden & storage benches", icon: "ri-subtract-line" },
      { label: "Accessories", to: "/products?category=Accessories", desc: "Umbrellas, carts & planters", icon: "ri-umbrella-line" },
    ],
  },
  {
    label: "Collections",
    dropdown: [
      { label: "Teak Collection", to: "/products?material=Teak", desc: "Sustainably sourced Grade A teak", icon: "ri-leaf-line" },
      { label: "Wicker & Rattan", to: "/products?material=Wicker", desc: "All-weather PE rattan designs", icon: "ri-circle-line" },
      { label: "Dining Sets", to: "/products?category=Sets", desc: "Complete outdoor dining solutions", icon: "ri-restaurant-line" },
      { label: "Lounge & Relax", to: "/products?category=Chairs", desc: "Ultimate relaxation furniture", icon: "ri-sun-line" },
      { label: "New Arrivals", to: "/products?badge=New+Arrival", desc: "Latest additions to our catalog", icon: "ri-sparkling-line" },
      { label: "Best Sellers", to: "/products?badge=Best+Seller", desc: "Our most popular pieces", icon: "ri-fire-line" },
    ],
  },
  {
    label: "About",
    dropdown: [
      { label: "Our Story", to: "/about", desc: "Who we are & our mission", icon: "ri-heart-line" },
      { label: "Quality Promise", to: "/about/quality", desc: "Materials & construction standards", icon: "ri-shield-check-line" },
      { label: "Customer Reviews", to: "/reviews", desc: "What our customers say", icon: "ri-star-line" },
      { label: "Contact Us", to: "/about/contact", desc: "Get in touch with our team", icon: "ri-mail-line" },
    ],
  },
  {
    label: "Reviews",
    to: "/reviews",
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const { totalItems } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const dropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  const navBg = isHome
    ? scrolled
      ? "bg-white border-b border-gray-100"
      : "bg-transparent"
    : "bg-white border-b border-gray-100";

  const textColor = isHome && !scrolled ? "text-white" : "text-[#1a1a1a]";
  const logoFilter = isHome && !scrolled ? "brightness-0 invert" : "";
  const subTextColor = isHome && !scrolled ? "text-white/70" : "text-gray-500";
  const hoverBg = isHome && !scrolled ? "hover:bg-white/10" : "hover:bg-gray-50";

  const handleMouseEnter = (label: string) => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimer.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setSearchOpen(false);
    }
  };

  return (
    <>
      {/* Top Info Bar */}
      <div className={`hidden lg:block fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isHome && !scrolled ? "bg-black/30" : "bg-[#2D5016]"}`}>
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="tel:+16025551234" className="flex items-center gap-1.5 text-white/90 text-xs hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-phone-line text-xs"></i></span>
              (602) 555-1234
            </a>
            <a href="mailto:aravellallc@outlook.com" className="flex items-center gap-1.5 text-white/90 text-xs hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-mail-line text-xs"></i></span>
              aravellallc@outlook.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/80 text-xs whitespace-nowrap">Free shipping on orders over $500</span>
            <div className="flex items-center gap-3">
              <a href="https://instagram.com" target="_blank" rel="nofollow noopener noreferrer" className="w-3.5 h-3.5 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer">
                <i className="ri-instagram-line text-xs"></i>
              </a>
              <a href="https://facebook.com" target="_blank" rel="nofollow noopener noreferrer" className="w-3.5 h-3.5 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer">
                <i className="ri-facebook-line text-xs"></i>
              </a>
              <a href="https://pinterest.com" target="_blank" rel="nofollow noopener noreferrer" className="w-3.5 h-3.5 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer">
                <i className="ri-pinterest-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`fixed left-0 right-0 z-40 transition-all duration-300 ${navBg} ${scrolled || !isHome ? "top-0 lg:top-9" : "top-0 lg:top-9"}`}>
        <div className="max-w-7xl mx-auto px-6 h-[68px] flex items-center justify-between gap-6">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/28bbe5f5-3a79-4a8a-a924-ccc2a95d56db.png"
              alt="ARAVELLA LLC"
              className={`h-9 w-auto object-contain transition-all duration-300 ${logoFilter}`}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.dropdown && handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                {item.to && !item.dropdown ? (
                  <Link
                    to={item.to}
                    className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium tracking-wide transition-all whitespace-nowrap cursor-pointer ${textColor} ${hoverBg}`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium tracking-wide transition-all whitespace-nowrap cursor-pointer ${textColor} ${hoverBg} ${activeDropdown === item.label ? (isHome && !scrolled ? "bg-white/10" : "bg-gray-50") : ""}`}
                  >
                    {item.label}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${activeDropdown === item.label ? "rotate-180" : ""}`}></i>
                    </span>
                  </button>
                )}

                {/* Dropdown */}
                {item.dropdown && activeDropdown === item.label && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white rounded-xl border border-gray-100 py-3 min-w-[320px] z-50"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="grid grid-cols-1 gap-0.5 px-2">
                      {item.dropdown.map((sub) => (
                        <Link
                          key={sub.to}
                          to={sub.to}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors group cursor-pointer"
                        >
                          <span className="w-8 h-8 flex items-center justify-center bg-[#f5f0e8] rounded-lg flex-shrink-0 group-hover:bg-[#2D5016]/10 transition-colors">
                            <i className={`${sub.icon} text-[#2D5016] text-sm`}></i>
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-[#1a1a1a] whitespace-nowrap">{sub.label}</div>
                            {sub.desc && <div className="text-xs text-gray-400 whitespace-nowrap">{sub.desc}</div>}
                          </div>
                          <span className="w-4 h-4 flex items-center justify-center ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            <i className="ri-arrow-right-s-line text-gray-400 text-sm"></i>
                          </span>
                        </Link>
                      ))}
                    </div>
                    {item.label === "Shop" && (
                      <div className="mt-2 mx-2 pt-2 border-t border-gray-100">
                        <Link
                          to="/products"
                          className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#2D5016] text-white text-sm font-medium rounded-lg hover:bg-[#3a6b1e] transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-store-2-line text-sm"></i></span>
                          View All 30 Products
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Search */}
            <div className="relative hidden md:block">
              {searchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center">
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search furniture..."
                    className="w-48 lg:w-56 h-9 pl-3 pr-9 text-sm border border-gray-200 rounded-full bg-white text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#2D5016] transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
                    className="absolute right-2.5 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className={`flex items-center justify-center w-9 h-9 rounded-md transition-all cursor-pointer ${textColor} ${hoverBg}`}
                >
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-search-line text-lg"></i></span>
                </button>
              )}
            </div>

            {/* Wishlist */}
            <button className={`hidden md:flex items-center justify-center w-9 h-9 rounded-md transition-all cursor-pointer ${textColor} ${hoverBg}`}>
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-heart-line text-lg"></i></span>
            </button>

            {/* Cart */}
            <Link
              to="/cart"
              className={`relative flex items-center justify-center w-9 h-9 rounded-md transition-all cursor-pointer ${textColor} ${hoverBg}`}
            >
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-shopping-bag-line text-lg"></i></span>
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#2D5016] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold leading-none">
                  {totalItems > 9 ? "9+" : totalItems}
                </span>
              )}
            </Link>

            {/* CTA Button - desktop only */}
            <Link
              to="/products"
              className="hidden lg:flex items-center gap-1.5 ml-2 px-4 py-2 bg-[#2D5016] text-white text-sm font-medium rounded-full hover:bg-[#3a6b1e] transition-colors cursor-pointer whitespace-nowrap"
            >
              Shop Now
            </Link>

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden flex items-center justify-center w-9 h-9 rounded-md transition-all cursor-pointer ${textColor} ${hoverBg}`}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto">
            {/* Mobile Search */}
            <div className="px-4 py-3 border-b border-gray-100">
              <form onSubmit={handleSearch} className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line text-sm"></i>
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search furniture..."
                  className="w-full h-10 pl-9 pr-4 text-sm border border-gray-200 rounded-full bg-gray-50 text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#2D5016]"
                />
              </form>
            </div>

            {/* Mobile Nav Items */}
            <div className="px-4 py-2">
              {navItems.map((item) => (
                <div key={item.label} className="border-b border-gray-50 last:border-0">
                  {item.to && !item.dropdown ? (
                    <Link
                      to={item.to}
                      className="flex items-center py-3 text-sm font-medium text-[#1a1a1a] cursor-pointer whitespace-nowrap"
                      onClick={() => setMobileOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <>
                      <button
                        className="flex items-center justify-between w-full py-3 text-sm font-medium text-[#1a1a1a] cursor-pointer"
                        onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                      >
                        <span className="whitespace-nowrap">{item.label}</span>
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${mobileExpanded === item.label ? "rotate-180" : ""}`}></i>
                        </span>
                      </button>
                      {mobileExpanded === item.label && item.dropdown && (
                        <div className="pb-2 pl-2">
                          {item.dropdown.map((sub) => (
                            <Link
                              key={sub.to}
                              to={sub.to}
                              className="flex items-center gap-3 py-2.5 text-sm text-gray-600 hover:text-[#2D5016] cursor-pointer"
                              onClick={() => setMobileOpen(false)}
                            >
                              <span className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-md flex-shrink-0">
                                <i className={`${sub.icon} text-[#2D5016] text-xs`}></i>
                              </span>
                              <span className="whitespace-nowrap">{sub.label}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile Bottom */}
            <div className="px-4 py-4 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-4 h-4 flex items-center justify-center text-[#2D5016]"><i className="ri-phone-line text-sm"></i></span>
                <a href="tel:+16025551234" className="text-sm text-gray-600 whitespace-nowrap">(602) 555-1234</a>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-4 h-4 flex items-center justify-center text-[#2D5016]"><i className="ri-mail-line text-sm"></i></span>
                <a href="mailto:aravellallc@outlook.com" className="text-sm text-gray-600 whitespace-nowrap">aravellallc@outlook.com</a>
              </div>
              <Link
                to="/products"
                className="flex items-center justify-center w-full py-3 bg-[#2D5016] text-white text-sm font-medium rounded-full cursor-pointer whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                Shop All Products
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Spacer for fixed navbar */}
      <div className={`${isHome ? "h-0" : "h-[68px] lg:h-[104px]"}`} />
    </>
  );
}
